import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, Upload, ListTodo, AlertOctagon, CheckCircle2, Users,
  CalendarClock, TrendingUp, Activity, Loader2, FileText, UserCheck, Check, Sparkles, RefreshCw,
} from "lucide-react";
import api, { fmtDate, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { CartesianGrid, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";

/**
 * Firm dashboard — one-shot overview of the accountant's firm state.
 * Complements the personal /app overview: this focuses on the client book.
 */
export default function FirmDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/back-office/firm-dashboard");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load firm dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="p-10 flex items-center gap-2 text-ink-lighter">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading firm dashboard…
      </div>
    );
  }
  if (!data) return null;

  const { kpis, clients, task_velocity, upcoming_deadlines, activity, specialty_matches = [], specialties = [] } = data;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-testid="firm-dashboard-page">
      <div>
        <div className="overline text-forest">Firm</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-1">
          How&apos;s your firm doing?
        </h1>
        <p className="text-ink-light mt-2 text-sm max-w-xl">
          A live snapshot of your client book — signals that need attention, work in flight, and what&apos;s ahead.
        </p>
      </div>

      <AiFirmSummary />

      {specialties.length > 0 && (
        <SpecialtyMatchesCard matches={specialty_matches} specialties={specialties} />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiTile label="Clients" value={kpis.total_clients} icon={Users} testid="kpi-clients" tone="sky" />
        <KpiTile label="Active (30d)" value={kpis.active_clients_30d} icon={CheckCircle2} testid="kpi-active" tone="forest" accent="text-forest" />
        <KpiTile label="Pending uploads" value={kpis.pending_uploads} icon={Upload} testid="kpi-pending" tone="ochre" accent={kpis.pending_uploads > 0 ? "text-ochre" : ""} />
        <KpiTile label="Open tasks" value={kpis.open_tasks} icon={ListTodo} testid="kpi-open-tasks" tone="violet" />
        <KpiTile label="Overdue" value={kpis.overdue_tasks} icon={AlertOctagon} testid="kpi-overdue" tone="rose" accent={kpis.overdue_tasks > 0 ? "text-ochre" : ""} />
        <KpiTile label="Done (30d)" value={kpis.tasks_done_30d} icon={TrendingUp} testid="kpi-done" tone="teal" accent="text-forest" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task velocity chart */}
        <div className="card-surface p-5 lg:col-span-2" data-testid="velocity-card">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline">Task velocity</div>
              <div className="font-heading text-lg font-medium">Last 4 weeks</div>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={task_velocity} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" vertical={false} />
                <XAxis dataKey="week_end" tick={{ fontSize: 11, fill: "#8A8A8A" }} tickLine={false} axisLine={false}
                  tickFormatter={(v) => fmtDate(v).slice(0, 5)} />
                <YAxis tick={{ fontSize: 11, fill: "#8A8A8A" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }}
                  labelFormatter={(v) => `Week ending ${fmtDate(v)}`}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="opened" name="Opened" fill="#B4741E" radius={[3, 3, 0, 0]} />
                <Bar dataKey="completed" name="Completed" fill="#0F4C3A" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Upcoming deadlines */}
        <div className="card-surface p-5" data-testid="deadlines-card">
          <div className="overline mb-1">Upcoming</div>
          <div className="font-heading text-lg font-medium mb-3">Tax deadlines</div>
          <ul className="space-y-3">
            {upcoming_deadlines.map((d) => (
              <li key={d.date} className="flex items-start gap-3" data-testid={`deadline-${d.date}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  d.days_away <= 14 ? "bg-ochre/10 text-ochre" : "bg-forest/10 text-forest"
                }`}>
                  <CalendarClock className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-snug">{d.label}</div>
                  <div className="text-xs text-ink-lighter num mt-0.5">
                    {fmtDate(d.date)} <span className="text-ink-lighter">·</span> in {d.days_away} day{d.days_away === 1 ? "" : "s"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Client health */}
      <div className="card-surface overflow-hidden" data-testid="client-health-card">
        <div className="px-5 py-4 border-b border-border flex items-baseline justify-between">
          <div>
            <div className="overline">Client health</div>
            <div className="font-heading text-lg font-medium">Sorted by attention needed</div>
          </div>
          <Link to="/app/clients" className="text-xs text-forest hover:underline">Manage clients →</Link>
        </div>
        {clients.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-lighter">No clients yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-paper text-xs uppercase text-ink-lighter tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Business</th>
                <th className="text-left px-5 py-3 font-medium">Last upload</th>
                <th className="text-left px-5 py-3 font-medium">Pending review</th>
                <th className="text-left px-5 py-3 font-medium">Open tasks</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {clients.map((c) => {
                const stale = c.days_since_last_upload !== null && c.days_since_last_upload > 30;
                const never = c.days_since_last_upload === null;
                const attentionLabel = c.pending_uploads > 0 ? "Uploads to review"
                  : never ? "Never uploaded"
                  : stale ? "Statement overdue"
                  : "Up to date";
                const attentionColor = c.pending_uploads > 0 ? "text-yellow-800 bg-yellow-100 border-yellow-300"
                  : never || stale ? "text-ochre bg-ochre/10 border-ochre/30"
                  : "text-forest bg-forest/10 border-forest/30";
                return (
                  <tr key={c.id} data-testid={`client-health-row-${c.id}`} className="hover:bg-paper/50">
                    <td className="px-5 py-3">
                      <div className="font-medium">{c.business_name}</div>
                      <div className="text-xs text-ink-lighter">{c.email}</div>
                    </td>
                    <td className="px-5 py-3">
                      {c.last_upload_at ? (
                        <div className="num text-xs">
                          {fmtDate(c.last_upload_at)}
                          <div className="text-ink-lighter">{c.days_since_last_upload}d ago</div>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-lighter">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 num">
                      {c.pending_uploads > 0 ? (
                        <span className="text-yellow-800 font-medium">{c.pending_uploads}</span>
                      ) : (
                        <span className="text-ink-lighter">0</span>
                      )}
                    </td>
                    <td className="px-5 py-3 num text-ink-light">{c.open_tasks}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-medium rounded-full border px-2 py-0.5 ${attentionColor}`}>
                        {attentionLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Activity feed */}
      <div className="card-surface p-5" data-testid="activity-card">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="overline">Live</div>
            <div className="font-heading text-lg font-medium">Recent activity</div>
          </div>
        </div>
        {activity.length === 0 ? (
          <div className="py-6 text-center text-sm text-ink-lighter">Nothing yet.</div>
        ) : (
          <ul className="space-y-3">
            {activity.map((a, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`activity-item-${i}`}>
                <ActivityIcon kind={a.kind} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-snug">
                    {a.kind === "upload" && (
                      <>
                        <span className="font-medium">{a.client_business_name || "Client"}</span>
                        <span className="text-ink-light"> uploaded </span>
                        <span className="font-mono text-xs">{a.detail}</span>
                        {a.received && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-forest bg-forest/10 rounded-full px-1.5 py-0.5"><Check className="w-2.5 h-2.5" strokeWidth={2.5}/>Received</span>}
                      </>
                    )}
                    {a.kind === "task_done" && (
                      <>
                        <span className="text-ink-light">Completed </span>
                        <span className="font-medium">{a.detail}</span>
                        {a.client_business_name && (
                          <>
                            <span className="text-ink-light"> for </span>
                            <span className="font-medium">{a.client_business_name}</span>
                          </>
                        )}
                      </>
                    )}
                    {a.kind === "impersonation" && (
                      <>
                        <span className="text-ink-light">Signed in as </span>
                        <span className="font-medium">{a.client_business_name || "a client"}</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-ink-lighter num mt-0.5">{fmtDateTime(a.at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const KPI_TONES = {
  forest: { bg: "bg-forest/10", border: "border-forest/20", icon: "text-forest", label: "text-forest" },
  sky:    { bg: "bg-sky-100",   border: "border-sky-200",   icon: "text-sky-700",   label: "text-sky-800" },
  ochre:  { bg: "bg-ochre/10",  border: "border-ochre/25",  icon: "text-ochre",     label: "text-ochre" },
  violet: { bg: "bg-violet-100",border: "border-violet-200",icon: "text-violet-700",label: "text-violet-800" },
  rose:   { bg: "bg-rose-100",  border: "border-rose-200",  icon: "text-rose-700",  label: "text-rose-800" },
  teal:   { bg: "bg-teal-100",  border: "border-teal-200",  icon: "text-teal-700",  label: "text-teal-800" },
};

function KpiTile({ label, value, icon: Icon, testid, accent = "", tone = "forest" }) {
  const t = KPI_TONES[tone] || KPI_TONES.forest;
  return (
    <div className={`rounded-2xl border ${t.border} ${t.bg} p-4 shadow-sm`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`text-[10px] uppercase tracking-wider font-medium ${t.label}`}>{label}</div>
        <Icon className={`w-3.5 h-3.5 ${t.icon}`} strokeWidth={1.75} />
      </div>
      <div className={`font-heading text-3xl num mt-1 ${accent || "text-ink"}`}>{value}</div>
    </div>
  );
}

function ActivityIcon({ kind }) {
  const cfg = {
    upload: { icon: FileText, bg: "bg-yellow-100", fg: "text-yellow-700" },
    task_done: { icon: CheckCircle2, bg: "bg-forest/10", fg: "text-forest" },
    impersonation: { icon: UserCheck, bg: "bg-ochre/10", fg: "text-ochre" },
  }[kind] || { icon: Activity, bg: "bg-paper", fg: "text-ink-lighter" };
  const Icon = cfg.icon;
  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg} ${cfg.fg}`}>
      <Icon className="w-4 h-4" strokeWidth={1.75} />
    </div>
  );
}

function AiFirmSummary() {
  const [summary, setSummary] = useState(null);
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async (force = false) => {
    setLoading(true);
    try {
      const { data } = await api.post("/back-office/firm-summary");
      setSummary(data.summary);
      setCached(data.cached);
      setLoaded(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI summary failed");
    } finally {
      setLoading(false);
    }
  };

  if (!loaded && !loading) {
    return (
      <div className="card-surface p-5 flex items-center justify-between gap-4" data-testid="ai-summary-cta">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="overline mb-0.5">AI advisor</div>
            <div className="font-heading text-lg font-medium leading-tight">Ask Claude for a 2-sentence firm health summary</div>
            <p className="text-sm text-ink-light mt-1">
              Grounded on today&apos;s real numbers. Cached daily, so re-opens are instant.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load(false)}
          className="btn-forest inline-flex items-center gap-2 shrink-0"
          data-testid="ai-summary-load-btn"
        >
          <Sparkles className="w-4 h-4" />
          Generate
        </button>
      </div>
    );
  }

  return (
    <div className="card-surface p-5" data-testid="ai-summary-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="overline">AI advisor · Firm health</div>
            <div className="flex items-center gap-2">
              {cached && <span className="text-[10px] text-ink-lighter">Cached today</span>}
              <button
                type="button"
                onClick={() => load(true)}
                disabled={loading}
                className="text-xs text-ink-lighter hover:text-forest inline-flex items-center gap-1"
                data-testid="ai-summary-refresh"
                title="Regenerate"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
          {loading && !summary ? (
            <div className="flex items-center gap-2 text-ink-lighter text-sm mt-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Analyzing your firm state…
            </div>
          ) : (
            <p className="text-sm text-ink leading-relaxed mt-1.5" data-testid="ai-summary-text">
              {summary}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function SpecialtyMatchesCard({ matches, specialties }) {
  // Read-only surface — the super-admin owns the routing decision, but this
  // gives the accountant early visibility that new work matching their
  // declared specialties has arrived on the platform.
  const count = matches.length;
  return (
    <div
      className={`card-surface p-5 ${count > 0 ? "border-forest/30 bg-forest/5" : ""}`}
      data-testid="specialty-matches-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="overline text-forest">Recommended fits</div>
              <div className="font-heading text-lg font-medium leading-tight">
                {count === 0
                  ? "No pending signups match your specialties"
                  : `${count} pending signup${count === 1 ? "" : "s"} match your specialt${specialties.length === 1 ? "y" : "ies"}`}
              </div>
            </div>
            <Link
              to="/app/settings"
              className="text-xs text-ink-lighter hover:text-forest underline decoration-dotted"
              data-testid="specialty-matches-edit-link"
            >
              Edit specialties
            </Link>
          </div>
          <p className="text-xs text-ink-light mt-1">
            Your firm is tagged with{" "}
            {specialties.map((s, i) => (
              <React.Fragment key={s}>
                <span className="font-medium text-ink">{s}</span>
                {i < specialties.length - 1 && ", "}
              </React.Fragment>
            ))}
            . The super-admin still routes final assignment.
          </p>

          {count > 0 && (
            <ul className="mt-4 space-y-2" data-testid="specialty-matches-list">
              {matches.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-3 rounded-lg bg-white border border-border px-3 py-2"
                  data-testid={`specialty-match-row-${m.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{m.business_name}</div>
                    <div className="text-[11px] text-ink-lighter flex items-center gap-2 mt-0.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-forest/10 text-forest px-2 py-0.5 font-medium">
                        {m.industry}
                      </span>
                      <span>Signed up {fmtDate(m.created_at)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

